import { apiService } from "./api.service";
import { AUTH, USERS } from "../config/api.endpoints";

const USER_KEY = "user";

export interface UserCredentials {
  username: string;
  password: string;
  email?: string;
}

export interface User {
  id: string | undefined;
  email: string | undefined;
  name: string | undefined;
  role: string | undefined;
  organization_id: string | undefined;
  organization_slug?: string | undefined;
  organization_is_setup_complete?: boolean | undefined;
  organization_is_demo?: boolean | undefined;
  organization_stripe_connect_onboarding_complete?: boolean | undefined;
  /** Préférence « Ignorer le suivi » : coupe l'analytique pour cet utilisateur. */
  ignore_tracking?: boolean | undefined;
  exp: number | undefined;
}

export interface RegisterPayload {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

/** Response from /users/me endpoint */
interface UserMeResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  organization_id: string;
  organization_slug?: string;
  organization_is_setup_complete?: boolean;
  organization_is_demo?: boolean;
  organization_stripe_connect_onboarding_complete?: boolean;
}

const authService = {
  async login(credentials: UserCredentials): Promise<boolean> {
    const params = new URLSearchParams();
    params.append("username", credentials.username);
    params.append("password", credentials.password);

    // FastAPI Users + CookieTransport renvoie 204 et pose un cookie, pas de body JSON
    await apiService.post(AUTH.LOGIN, params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    // Vérifier la session via /users/me (cookie-based)
    try {
      const me = await apiService.get(USERS.ME);
      return !!me;
    } catch (error: unknown) {
      // Login a échoué, nettoyer la session locale
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        this.clearLocalSession();
      } else {
      await this.logout();
      }
      return false;
    }
  },

  async fetchCurrentUser(): Promise<User | null> {
    try {
      const response = await apiService.get<UserMeResponse>(USERS.ME);

      const user: User = {
        id: response.id,
        email: response.email,
        name: `${response.first_name || ""} ${response.last_name || ""}`.trim(),
        role: response.role,
        organization_id: response.organization_id,
        organization_slug: response.organization_slug,
        organization_is_setup_complete: response.organization_is_setup_complete,
        organization_is_demo: response.organization_is_demo,
        organization_stripe_connect_onboarding_complete:
          response.organization_stripe_connect_onboarding_complete,
        exp: undefined,
      };

      localStorage.setItem(USER_KEY, JSON.stringify(user));
      return user;
    } catch (error: unknown) {
      // Si erreur 401, l'utilisateur n'était pas connecté - nettoyage local seulement
      // Pas besoin d'appeler logout() côté serveur (évite une 2ème erreur 401)
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        this.clearLocalSession();
      } else {
        // Pour les autres erreurs (réseau, 500, etc.), on fait un logout complet
        this.logout();
      }
      return null;
    }
  },

  async register(userData: RegisterPayload): Promise<User> {
    return await apiService.post<User>(AUTH.REGISTER, userData);
  },

  async forgotPassword(email: string): Promise<void> {
    await apiService.post(AUTH.FORGOT_PASSWORD, { email });
  },

  async resetPassword(password: string, token: string): Promise<void> {
    await apiService.post(AUTH.RESET_PASSWORD, { token, password });
  },

  /**
   * Nettoie la session locale sans appeler le backend.
   * Utilisé quand on sait déjà que la session n'existe pas côté serveur (401).
   */
  clearLocalSession(): void {
    try {
      localStorage.removeItem(USER_KEY);
    } catch (error: unknown) {
      // Gracefully handle localStorage unavailability
      console.warn("localStorage not available during session clear", error);
    }
  },

  async logout(): Promise<void> {
    try {
      // Invalider la session côté serveur (FastAPI Users CookieTransport)
      try {
        await apiService.post(AUTH.LOGOUT, {});
      } catch (error: unknown) {
        // Keep local logout resilient if backend call fails.
        console.warn("Logout API call failed", error);
      }

      // Nettoyage côté client
      this.clearLocalSession();
    } finally {
      // Pas d'exception propagée pour faciliter l'UX
    }
  },

  isAuthenticated(): boolean {
    // Authentification basée sur cookies uniquement, plus de tokens locaux
    return false;
  },

  getToken(): string | null {
    // Plus de tokens locaux utilisés pour l'authentification
    return null;
  },

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) {
      return null;
    }

    try {
      if (!this.isAuthenticated()) {
        this.logout();
        return null;
      }
      return JSON.parse(userStr) as User;
    } catch {
      return null;
    }
  },

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return !!user && user.role === role;
  },

  isOrganizationAdmin(): boolean {
    return this.hasRole("ORGANIZATION_ADMIN");
  },

  isSuperAdmin(): boolean {
    return this.hasRole("SUPER_ADMIN");
  },

  isFisher(): boolean {
    return this.hasRole("USER");
  },

  // Cette fonction est maintenant dans authStore, mais on la garde ici pour l'instant

  async finalizeStripeConnect(_code: string): Promise<void> {
    // Note: STRIPE_CONNECT.FINALIZE doit être un endpoint valide dans api.endpoints.ts
    // return apiService.post(STRIPE_CONNECT.FINALIZE, { code });
  },

  /**
   * Génère l'URL de l'endpoint d'autorisation Google OAuth
   * @param redirectUrl URL de redirection après authentification (optionnel)
   * @returns URL de l'endpoint backend pour l'autorisation Google
   */
  getGoogleAuthEndpoint(redirectUrl?: string): string {
    const base = `${apiService.axios.defaults.baseURL}/auth/google/authorize`;
    if (redirectUrl && redirectUrl !== '/') {
      return `${base}?redirect=${encodeURIComponent(redirectUrl)}`;
    }
    return base;
  },

  /**
   * Redirige vers l'authentification Google OAuth
   * FastAPI Users retourne un JSON avec authorization_url, on doit donc
   * d'abord faire un appel API pour obtenir cette URL puis rediriger.
   * @param redirectUrl URL de redirection après authentification (optionnel)
   */
  async redirectToGoogleAuth(redirectUrl?: string): Promise<void> {
    try {
      const endpoint = this.getGoogleAuthEndpoint(redirectUrl);
      // FastAPI Users retourne { authorization_url: "https://accounts.google.com/..." }
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`OAuth endpoint returned ${response.status}`);
      }

      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        throw new Error('No authorization_url in response');
      }
    } catch (error) {
      console.error('Failed to initiate Google OAuth:', error);
      throw error;
    }
  },
};

export { authService };
